// A hash router: #/scan, #/wine/123, #/search?producer=… — no server config
// needed under any base path, which matters because the same bundle is
// served at / by Vite and under /proef/ by druif. Navigation is deliberately
// synchronous: wrapping it in a View Transition froze taps for the snapshot
// plus ~340ms of animation on every screen change.
import { useSyncExternalStore } from "react";

export interface Route {
  path: string;
  segments: string[];
  params: URLSearchParams;
}

function parse(hash: string): Route {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  const [pathPart = "/", query = ""] = raw.split("?", 2);
  const path = pathPart.startsWith("/") ? pathPart : `/${pathPart}`;
  return {
    path,
    segments: path.split("/").filter((s) => s !== ""),
    params: new URLSearchParams(query),
  };
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}

function snapshot(): string {
  return window.location.hash;
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, snapshot, () => "");
  return parse(hash);
}

export function navigate(path: string, options: { replace?: boolean } = {}): void {
  const target = `#${path.startsWith("/") ? path : `/${path}`}`;
  if (options.replace) {
    // nosemgrep: rules.javascript.browser.security.js-open-redirect-from-function -- target is always "#" + a path (line above), so this only ever rewrites the current page's own hash fragment and can't navigate to another origin.
    window.location.replace(target);
  } else {
    window.location.hash = target;
  }
}

export function back(fallback = "/"): void {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    navigate(fallback, { replace: true });
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Reads a value stashed on the current history entry; unlike module state or a consumed
 * sessionStorage key it survives Back/Forward and a reload of exactly that entry. */
export function readEntryState(key: string): unknown {
  const state: unknown = window.history.state;
  return isRecord(state) ? state[key] : undefined;
}

/** Stashes a structured-cloneable value on the current history entry without adding one. */
export function writeEntryState(key: string, value: unknown): void {
  const state: unknown = window.history.state;
  try {
    window.history.replaceState({ ...(isRecord(state) ? state : {}), [key]: value }, "");
  } catch {
    // A sandboxed or quota-limited history only loses the restore on Back, not the screen.
  }
}
